import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type { NotificationsResponse } from '../types';

/**
 * Notification feed for the current user. The backend has no real-time channel
 * for notifications (socket.io is whiteboard-only), so we poll.
 */
export function useNotifications(limit = 20) {
  return useQuery({
    queryKey: ['notifications', limit],
    queryFn: async () => {
      const { data } = await api.get<NotificationsResponse>(`/api/notifications?limit=${limit}`);
      return data;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useNotificationMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/notifications/${id}/read`);
      return data;
    },
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ ok: boolean }>(`/api/notifications/read-all`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { markRead, markAllRead };
}
